import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import { PROXY } from "../../lib/utils";
import { Input, Button, Typography, Card } from "@material-tailwind/react";
import useAuth from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const getValidationSchema = (t) =>
  Yup.object().shape({
    fullName: Yup.string()
      .required(t("validation.name_required"))
      .min(3, t("validation.name_min"))
      .max(50, t("validation.name_max"))
      .matches(/^[A-Za-z\u0600-\u06FF]/, t("validation.name_start_letter")),
    email: Yup.string()
      .email(t("validation.email_invalid"))
      .required(t("validation.email_required")),
  });

const Info = () => {
  const { user, accessToken, refreshUser } = useAuth();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(getValidationSchema(t)),
  });

  useEffect(() => {
    if (user) {
      reset({
        fullName: user.fullName,
        email: user.email,
      });
    }
  }, [user, reset]);

  const onSubmit = async (formData) => {
    try {
      await axios.put(
        `${PROXY}/users/me`,
        {
          fullName: formData.fullName,
          email: formData.email,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      await refreshUser();
      toast.success(t("Profile-updated-successfully!"));
    } catch (error) {
      if (!navigator.onLine) {
        toast.error(t("toast.internet-error"));
      } else if (error.response?.data?.errors?.length > 0) {
        error.response.data.errors.forEach((err) => {
          setError(err.path, { type: "manual", message: err.msg });
        });
      } else {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          t("Something-went-wrong.");
        toast.error(message);
      }
    }
  };

  const handleTeacherRoleRequest = async () => {
    try {
      const { data } = await axios.post(
        `${PROXY}/users/me/teacher-request`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      toast.success(
        data?.message ||
          t("teacher.request_success", "Teacher request submitted."),
      );
      await refreshUser();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        t("teacher.request_error", "Unable to submit teacher request.");
      toast.error(message);
    }
  };

  const teacherRequestStatus = user?.teacherRequest?.status || null;
  const isTeacher = user?.role === "teacher";
  const canRequestTeacherRole =
    !isTeacher && teacherRequestStatus !== "pending";

  return (
    <Card className="w-full shadow-lg border border-gray-100">
      {/* Neutral Header */}
      <div className="p-6 border-b border-gray-200 bg-white rounded-t-lg">
        <Typography variant="h4" className="font-bold text-gray-800">
          {t("Profile-Information")}
        </Typography>
        <Typography variant="small" className="text-gray-500 mt-1">
          {t("Update-your-basic-details-below")}
        </Typography>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-6">
        {/* Full Name */}
        <div>
          <label className="block mb-1 text-sm font-medium">
            {t("form.your-full-name")}
          </label>
          <Input
            {...register("fullName")}
            size="lg"
            color={errors.fullName ? "red" : "indigo"}
            error={errors?.fullName}
            placeholder={t("form.your-full-name")}
            containerProps={{ className: "w-full" }}
          />
          {errors.fullName && (
            <Typography color="red" className="text-sm mt-1">
              {errors.fullName.message}
            </Typography>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block mb-1 text-sm font-medium">
            {t("form.your-email")}
          </label>
          <Input
            {...register("email")}
            size="lg"
            color={errors.email ? "red" : "indigo"}
            error={errors?.email}
            placeholder={t("form.your-email")}
            containerProps={{ className: "w-full" }}
          />
          {errors.email && (
            <Typography color="red" className="text-sm mt-1">
              {errors.email.message}
            </Typography>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <Button
            type="submit"
            size="md"
            variant="gradient"
            color="indigo"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 py-2 font-semibold"
          >
            {isSubmitting ? t("Updating") + "..." : t("Update-Profile")}
          </Button>
        </div>
      </form>

      {/* <div className="border-t border-gray-200 p-4 sm:p-6"> */}
      {/* <Typography variant="h6" className="font-semibold text-gray-800">
          {t("teacher.role_section_title", "Teacher Access")}
        </Typography>
        <Typography variant="small" className="mt-1 text-gray-500">
          {isTeacher
            ? t("teacher.role_section_teacher", "Your account can manage courses and still use student features.")
            : t("teacher.role_section_student", "Request teacher access if you need to create and manage your own courses.")}
        </Typography> */}

      {/* <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {isTeacher
                  ? t("teacher.current_role_teacher", "Current role: Teacher")
                  : t("teacher.current_role_student", "Current role: Student")}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {teacherRequestStatus
                  ? t(
                      `teacher.status_${teacherRequestStatus}`,
                      `Teacher request status: ${teacherRequestStatus}`,
                    )
                  : t(
                      "teacher.no_request",
                      "No teacher request submitted yet.",
                    )}
              </p>
            </div>
            {canRequestTeacherRole ? (
              <Button
                type="button"
                color="indigo"
                onClick={handleTeacherRoleRequest}
              >
                {t("teacher.request_role_button", "Request Teacher Role")}
              </Button>
            ) : null}
          </div>
        </div> */}
      {/* </div> */}
    </Card>
  );
};

export default Info;
